import React, { useState, useEffect } from "react";
import { useTimingData } from "./hooks";
import { TimeData } from "./utils/timingUtils";

const TimeBar: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<
    "daily" | "weekly" | "monthly"
  >("daily");
  const [animateBars, setAnimateBars] = useState(false);
  const { data, loading, error } = useTimingData();

  // Utiliser les données du backend
  const currentData: TimeData[] = data[selectedPeriod] || [];

  // Calculer les statistiques
  const maxValue =
    currentData.length > 0
      ? Math.max(...currentData.map((item: TimeData) => item.value))
      : 0;
  const meanValue =
    currentData.length > 0
      ? currentData.reduce(
          (sum: number, item: TimeData) => sum + item.value,
          0,
        ) / currentData.length
      : 0;
  const meanHours = Math.floor(meanValue);
  const meanMinutes = Math.floor((meanValue - meanHours) * 60);

  console.log("Statistiques calculées:", {
    maxValue,
    meanValue,
    meanHours,
    meanMinutes,
  });

  // Fonction pour formater le temps de manière standardisée
  const formatTime = (hours: number, minutes: number, seconds: number) => {
    // Les données sont déjà standardisées, on affiche de manière claire
    if (hours > 0) {
      if (minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${hours}h`;
      }
    } else if (minutes > 0) {
      if (seconds > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${minutes}m`;
      }
    } else if (seconds > 0) {
      return `${seconds}s`;
    } else {
      return "0s";
    }
  };

  const handlePeriodChange = (period: "daily" | "weekly" | "monthly") => {
    console.log("Changement de période vers:", period);
    setSelectedPeriod(period);
    setAnimateBars(false);
    // Relancer l'animation après le changement
    setTimeout(() => setAnimateBars(true), 100);
  };

  // Démarrer l'animation quand les données changent
  useEffect(() => {
    if (!loading && currentData.length > 0) {
      setAnimateBars(true);
    }
  }, [loading, currentData]);

  // Afficher le chargement
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#131CC9] mx-auto mb-4"></div>
          <div className="text-gray-500">Chargement des statistiques...</div>
        </div>
      </div>
    );
  }

  // Afficher l'erreur ou l'absence de données
  if (error || currentData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <div className="text-gray-600 font-medium mb-2">
            {error
              ? "Aucune donnée de temps enregistrée"
              : "Aucune donnée disponible"}
          </div>
          <div className="text-gray-500 text-sm">
            {error
              ? error
              : "Aucune statistique de temps disponible pour cette période."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-8 space-y-4 lg:space-y-0">
        {/* Section temps moyen */}
        <div className="flex-1">
          <h3 className="text-[#131CC9] text-lg font-bold mb-3">
            Temps moyen passé
          </h3>
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">
                {meanHours}
              </div>
              <div className="text-sm text-gray-500 font-medium">heures</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">
                {meanMinutes}
              </div>
              <div className="text-sm text-gray-500 font-medium">minutes</div>
            </div>
          </div>
        </div>

        {/* Sélecteur de période */}
        <div className="lg:w-48">
          <select
            value={selectedPeriod}
            onChange={(e) =>
              handlePeriodChange(
                e.target.value as "daily" | "weekly" | "monthly",
              )
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#131CC9] focus:border-transparent"
          >
            <option value="daily">Journalière</option>
            <option value="weekly">Hebdomadaire</option>
            <option value="monthly">Mensuel</option>
          </select>
        </div>
      </div>

      {/* Graphique en barres horizontales style parents */}
      <div className="space-y-4">
        {currentData.map((item: TimeData, index: number) => {
          const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
          const displayValue = formatTime(
            item.hours,
            item.minutes,
            item.seconds,
          );

          console.log(
            `Barre ${item.label}: valeur=${item.value}, pourcentage=${percentage}%, affichage=${displayValue}`,
          );

          return (
            <div key={index} className="space-y-2 group">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">{item.label}</span>
                <span className="text-gray-500">
                  {Number.isFinite(item.value) ? displayValue : "Néant"}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden">
                <div
                  className="bg-gradient-to-r from-[#131CC9] to-[#4F46E5] h-3 rounded-full transition-all duration-1000 ease-out relative"
                  style={{
                    width: animateBars
                      ? `${Number.isFinite(percentage) ? percentage : 0}%`
                      : "0%",
                    transitionDelay: `${index * 100}ms`,
                  }}
                >
                  {/* Effet de brillance */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-50"></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Informations supplémentaires */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-500 text-center">
          {currentData.filter((item: TimeData) => item.value > 0).length}{" "}
          période(s) avec activité
        </div>
      </div>
    </div>
  );
};

export default TimeBar;
